// External
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import i18n from '@ohif/i18n';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import Compose from './routes/Mode/Compose';
import {
  API_AUTH_ERROR_EVENT,
  ServicesManager,
  ExtensionManager,
  CommandsManager,
  HotkeysManager,
  ServiceProvidersManager,
} from '@ohif/core';
import {
  AlertConnection,
  DialogProvider,
  Modal,
  ModalProvider,
  SnackbarProvider,
  ThemeWrapper,
  ViewportDialogProvider,
  ViewportGridProvider,
  CineProvider,
  UserAuthenticationProvider,
  ToolboxProvider,
  SegmentationStateProvider,
  usePatient,
} from '@ohif/ui';

// Viewer Project
import { AppConfigProvider } from './state';
import createRoutes from './routes';
import appInit from './appInit.js';
import OpenIdConnectRoutes from './utils/OpenIdConnectRoutes';
import { getValidateTicketToken } from './utils/getValidateToken';
import { checkLogin } from './utils/checkLogin';
import SessionErrorModal from './components/SessionErrorModal';

let commandsManager: CommandsManager,
  extensionManager: ExtensionManager,
  servicesManager: ServicesManager,
  serviceProvidersManager: ServiceProvidersManager,
  hotkeysManager: HotkeysManager;

type PatientContextSetter = (data: {
  patient?: unknown;
  project?: unknown;
  alias?: unknown;
}) => void;

type AuthServiceLike = {
  setServiceImplementation?: (implementation: {
    getAuthorizationHeader: () => Record<string, string>;
  }) => void;
  waitForAuthReady?: (options: { timeoutMs: number; pollIntervalMs: number }) => Promise<void>;
};

type InitData = {
  commandsManager: CommandsManager;
  extensionManager: ExtensionManager;
  servicesManager: ServicesManager;
  serviceProvidersManager: ServiceProvidersManager;
  hotkeysManager: HotkeysManager;
  appConfig: any;
  error?: string;
};

type AuthWarningDetail = {
  message?: string;
};

type SessionErrorDetail = {
  isViewerTokenError?: boolean;
  message?: string;
  status?: number;
};

type NetworkPatchState = {
  originalOpen: typeof XMLHttpRequest.prototype.open;
  originalSend: typeof XMLHttpRequest.prototype.send;
  originalSetRequestHeader: typeof XMLHttpRequest.prototype.setRequestHeader;
  originalFetch: typeof window.fetch;
};

declare global {
  interface Window {
    __VIDAA_NETWORK_PATCH__?: NetworkPatchState;
  }
}

function getSessionViewerToken(): string | null {
  let token = null;

  try {
    token = sessionStorage.getItem('session_viewer');
  } catch {
    token = null;
  }

  if (
    (!token || token === 'undefined' || token === 'null') &&
    typeof localStorage !== 'undefined'
  ) {
    try {
      token = localStorage.getItem('session_viewer');
    } catch {
      token = null;
    }
  }

  if (!token || token === 'undefined' || token === 'null') {
    return null;
  }

  return token;
}

function normalizeTokenForDebug(rawToken: unknown): string | null {
  if (!rawToken) {
    return null;
  }

  let token = `${rawToken}`.trim();

  if (!token || token === 'undefined' || token === 'null') {
    return null;
  }

  if (token.startsWith('"') && token.endsWith('"')) {
    token = token.slice(1, -1).trim();
  }

  if (/^Bearer\s+/i.test(token)) {
    token = token.replace(/^Bearer\s+/i, '').trim();
  }

  return token || null;
}

function getTokenDebugInfo(rawToken: unknown) {
  const token = normalizeTokenForDebug(rawToken);

  return {
    hasToken: Boolean(token),
    tokenLength: token?.length || 0,
    tokenPreview: token ? `${token.slice(0, 8)}...${token.slice(-8)}` : null,
  };
}

async function waitForSessionViewerToken(timeoutMs = 1000): Promise<string | null> {
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const token = getSessionViewerToken();

    if (token) {
      return token;
    }

    await new Promise(resolve => setTimeout(resolve, 50));
  }

  return getSessionViewerToken();
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function isOrthancUrl(url: string, orthancUrl: string): boolean {
  try {
    const orthancOrigin = new URL(orthancUrl, window.location.origin).origin;
    const requestOrigin = new URL(url, window.location.origin).origin;

    return requestOrigin === orthancOrigin;
  } catch {
    return url.startsWith(orthancUrl);
  }
}

function isDicomWebUrl(url: string): boolean {
  return (
    url.includes('/studies') ||
    url.includes('/series') ||
    url.includes('/instances') ||
    url.includes('/metadata') ||
    url.includes('/frames')
  );
}

/**
 * Checks session validity and updates patient context state.
 */
async function handleUserSessionHydration(
  temporalToken: string | null,
  setData: PatientContextSetter
): Promise<boolean> {
  const viewerTokenExists = Boolean(getSessionViewerToken());
  const shouldCheckLogin = viewerTokenExists || Boolean(temporalToken);

  if (!shouldCheckLogin) {
    console.warn('App.tsx: no session_viewer or ticket, skipping checkLogin');
    return false;
  }

  const loginData = await checkLogin();

  if (!loginData) {
    return false;
  }

  setData({
    patient: loginData.patient,
    project: loginData.project,
    alias: loginData.alias,
  });

  return true;
}

/**
 * Injects global interceptors for raw XMLHttpRequest and fetch calls.
 */
function applyGlobalNetworkPatches(): void {
  if (process.env.CLOUD !== 'AWS' || window.__VIDAA_NETWORK_PATCH__) {
    return;
  }

  const ORTHANC_ORIGIN = process.env.ORTHANC_URL || 'https://orthanc.dev.virtest.eu';

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
  const originalFetch = window.fetch.bind(window);

  window.__VIDAA_NETWORK_PATCH__ = {
    originalOpen,
    originalSend,
    originalSetRequestHeader,
    originalFetch,
  };

  XMLHttpRequest.prototype.open = function patchedOpen(
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null
  ) {
    const requestUrl = typeof url === 'string' ? url : url?.toString?.() || '';
    const shouldPatch = isOrthancUrl(requestUrl, ORTHANC_ORIGIN);

    (
      this as XMLHttpRequest & {
        __vidaa_request_url__?: string;
        __vidaa_should_patch__?: boolean;
      }
    ).__vidaa_request_url__ = requestUrl;

    (
      this as XMLHttpRequest & {
        __vidaa_should_patch__?: boolean;
      }
    ).__vidaa_should_patch__ = shouldPatch;

    return originalOpen.call(
      this,
      method,
      url as string,
      async ?? true,
      username ?? null,
      password ?? null
    );
  };

  XMLHttpRequest.prototype.setRequestHeader = function patchedSetRequestHeader(
    header: string,
    value: string
  ) {
    const xhr = this as XMLHttpRequest & {
      __vidaa_request_url__?: string;
      __vidaa_should_patch__?: boolean;
    };

    if (xhr.__vidaa_should_patch__ && header.toLowerCase() === 'authorization') {
      return;
    }

    return originalSetRequestHeader.call(this, header, value);
  };

  XMLHttpRequest.prototype.send = function patchedSend(
    ...args: Parameters<XMLHttpRequest['send']>
  ) {
    try {
      const xhr = this as XMLHttpRequest & {
        __vidaa_request_url__?: string;
        __vidaa_should_patch__?: boolean;
      };

      if (xhr.__vidaa_should_patch__) {
        const token = getSessionViewerToken();

        xhr.withCredentials = true;

        if (token) {
          originalSetRequestHeader.call(xhr, 'Authorization', `Bearer ${token}`);
        }
      }
    } catch (error) {
      console.warn('Could not force credentials/headers in XHR', error);
    }

    return originalSend.apply(this, args);
  };

  window.fetch = function patchedFetch(input: RequestInfo | URL, init?: RequestInit) {
    const url =
      typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;

    const shouldPatch = isOrthancUrl(url, ORTHANC_ORIGIN);

    if (!shouldPatch) {
      return originalFetch(input, init);
    }

    const request = input instanceof Request ? input : undefined;
    const headers = new Headers(init?.headers || request?.headers || {});
    const token = getSessionViewerToken();
    const previousAuthorization = headers.get('Authorization');

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    } else {
      headers.delete('Authorization');
    }

    return originalFetch(input, {
      ...init,
      credentials: 'include',
      headers,
    });
  };
}

/**
 * Connects the session token to OHIF's authentication service layer.
 */
function configureUserAuthenticationService(initData: InitData): void {
  if (process.env.CLOUD !== 'AWS') {
    return;
  }

  const authService = initData.servicesManager.services
    .userAuthenticationService as AuthServiceLike;

  authService.setServiceImplementation?.({
    getAuthorizationHeader: () => {
      const token = getSessionViewerToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
  });
}

/**
 * Handles baseline application path rewriting to safe targets.
 */
function handleRoutingSanitization(initData: InitData): void {
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  const base = initData.appConfig.routerBasename || '/';

  const targetPath = `${base}/measures3D-seg`.replace(/\/+/g, '/').replace(/\/+$/, '') || '/';

  const viewerRedirectPath =
    `${base}/viewer-redirect`.replace(/\/+/g, '/').replace(/\/+$/, '') || '/';

  if (pathname !== targetPath && pathname !== viewerRedirectPath) {
    window.history.replaceState({}, document.title, targetPath);
  }
}

/**
 * Calculates WebGL max 3D texture size capabilities.
 */
function computeMax3DTextureCapabilities(appConfigState: any): void {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2');

  if (gl) {
    appConfigState.max3DTextureSize = gl.getParameter(gl.MAX_3D_TEXTURE_SIZE);
  }
}

function removeTicketFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('ticket');
  window.history.replaceState({}, document.title, url.toString());
}

/**
 * Validates the temporal ticket token from URL parameters if present.
 */
async function handleTicketValidation(
  temporalToken: string | null,
  setData: PatientContextSetter
): Promise<boolean> {
  if (!temporalToken) {
    return false;
  }

  const maxAttempts = 2;
  let validationData = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      validationData = await getValidateTicketToken(temporalToken, setData);
      if (validationData) {
        break;
      }
    } catch (error) {
      console.warn('[AUTH] Ticket validation attempt failed', {
        attempt,
        maxAttempts,
        error,
      });
    }

    if (attempt < maxAttempts) {
      await sleep(350);
    }
  }

  if (!validationData) {
    return false;
  }

  const validationToken = normalizeTokenForDebug(validationData?.token_viewer);
  const storedTokenBeforeWait = normalizeTokenForDebug(sessionStorage.getItem('session_viewer'));

  const settledToken = await waitForSessionViewerToken(1000);
  const normalizedSettledToken = normalizeTokenForDebug(settledToken);

  return true;
}

function App({ config, defaultExtensions, defaultModes }) {
  const [init, setInit] = useState<InitData | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [authWarning, setAuthWarning] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<SessionErrorDetail | null>(null);

  const initStartedRef = useRef(false);
  const { setData } = usePatient();

  useEffect(() => {
    if (initStartedRef.current) {
      return;
    }

    initStartedRef.current = true;

    let cancelled = false;

    async function initAuth() {
      try {
        setIsAuthenticating(true);

        const urlParams = new URLSearchParams(window.location.search);
        const temporalToken = urlParams.get('ticket');
        const studyInstanceUID =
          urlParams.get('StudyInstanceUIDs') || urlParams.get('studyInstanceUID') || temporalToken;

        // Added a third fallback check: if session_viewer token exists, do not trigger a navigation error
        if (!studyInstanceUID && !temporalToken && !getSessionViewerToken()) {
          setInit({
            error: 'No study or session parameters were found in the request.',
          } as InitData);
          return;
        }

        const ticketValidated = await handleTicketValidation(temporalToken, setData);

        // A ticket validated in this bootstrap cycle is enough reason to hydrate
        // the backend session, even if sessionStorage is not readable yet.
        const sessionHydrated = await handleUserSessionHydration(temporalToken, setData);

        // Keep ticket in URL if validation failed, so reload can retry bootstrap.
        if (temporalToken && ticketValidated) {
          removeTicketFromUrl();
        } else if (temporalToken && !ticketValidated) {
          console.warn('[AUTH] ticket kept in URL to allow retry on reload');
        }

        // Must happen before appInit because OHIF dataSources may request
        // DICOMweb metadata during appInit.
        applyGlobalNetworkPatches();

        const initData = await appInit(config, defaultExtensions, defaultModes);

        configureUserAuthenticationService(initData);

        handleRoutingSanitization(initData);
        computeMax3DTextureCapabilities(initData.appConfig);

        const authService = initData.servicesManager.services
          .userAuthenticationService as AuthServiceLike;

        await authService.waitForAuthReady?.({
          timeoutMs: 5000,
          pollIntervalMs: 50,
        });

        if (!cancelled) {
          setInit(initData);
        }
      } catch (error) {
        console.error('Critical error while initializing application:', error);

        if (!cancelled) {
          setInit({
            error: 'The viewer could not initialize the secure session.',
          } as InitData);
        }
      } finally {
        if (!cancelled) {
          setIsAuthenticating(false);
        }
      }
    }

    initAuth();

    return () => {
      cancelled = true;
    };
  }, [config, defaultExtensions, defaultModes, setData]);

  useEffect(() => {
    const onAuthWarning = (event: Event) => {
      const customEvent = event as CustomEvent<AuthWarningDetail>;
      const message = customEvent?.detail?.message;
      if (message) {
        setAuthWarning(message);
      }
    };

    window.addEventListener('vidaa-auth-warning', onAuthWarning as EventListener);

    return () => {
      window.removeEventListener('vidaa-auth-warning', onAuthWarning as EventListener);
    };
  }, []);

  useEffect(() => {
    const onSessionError = (event: Event) => {
      const detail = (event as CustomEvent<SessionErrorDetail>).detail;
      const message = detail?.message || 'Your session has expired.';

      setSessionError({
        isViewerTokenError:
          detail?.isViewerTokenError ?? message.toLowerCase().includes('viewer'),
        message,
        status: detail?.status,
      });
    };

    window.addEventListener(API_AUTH_ERROR_EVENT, onSessionError);

    return () => {
      window.removeEventListener(API_AUTH_ERROR_EVENT, onSessionError);
    };
  }, []);

  const sessionErrorModal = sessionError ? (
    <SessionErrorModal
      isViewerTokenError={Boolean(sessionError.isViewerTokenError)}
      message={sessionError.message || 'Your session has expired.'}
      status={sessionError.status}
      redirectUrl={process.env.BROWSER_URL}
    />
  ) : null;

  if (isAuthenticating) {
    return (
      <div
        style={{
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#101524',
          color: '#fff',
          fontFamily: 'sans-serif',
        }}
      >
        {sessionErrorModal}
        {authWarning ? (
          <div
            style={{
              position: 'fixed',
              top: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: '#7a2f2f',
              color: '#fff',
              padding: '10px 14px',
              borderRadius: '6px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
              maxWidth: '80vw',
              zIndex: 1100,
            }}
          >
            {authWarning}
          </div>
        ) : null}
        <h2>Initializing secure session in the viewer...</h2>
      </div>
    );
  }

  if (!init) {
    return null;
  }

  if (init.error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#101524',
          color: '#ff6b6b',
          fontFamily: 'sans-serif',
        }}
      >
        {sessionErrorModal}
        <h2>Navigation Error</h2>
        <p style={{ color: '#aaa', marginTop: '10px' }}>{init.error}</p>
        <button
          onClick={() => {
            if (process.env.BROWSER_URL) {
              window.location.href = process.env.BROWSER_URL;
            }
          }}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            cursor: 'pointer',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            backgroundColor: isHovered ? '#2c3e50' : '#34495e',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          Go back to browser
        </button>
      </div>
    );
  }

  commandsManager = init.commandsManager;
  extensionManager = init.extensionManager;
  servicesManager = init.servicesManager;
  serviceProvidersManager = init.serviceProvidersManager;
  hotkeysManager = init.hotkeysManager;

  const appConfigState = init.appConfig;
  const { routerBasename, modes, dataSources, oidc, showStudyList } = appConfigState;

  const {
    uiDialogService,
    uiModalService,
    uiNotificationService,
    uiViewportDialogService,
    viewportGridService,
    cineService,
    userAuthenticationService,
    customizationService,
  } = servicesManager.services;

  const providers = [
    [AppConfigProvider, { value: appConfigState }],
    [UserAuthenticationProvider, { service: userAuthenticationService }],
    [I18nextProvider, { i18n }],
    [ThemeWrapper],
    [ToolboxProvider],
    [SegmentationStateProvider],
    [ViewportGridProvider, { service: viewportGridService }],
    [ViewportDialogProvider, { service: uiViewportDialogService }],
    [CineProvider, { service: cineService }],
    [SnackbarProvider, { service: uiNotificationService, isDisabled: Boolean(sessionError) }],
    [DialogProvider, { service: uiDialogService }],
    [ModalProvider, { service: uiModalService, modal: Modal }],
  ];

  Object.entries(serviceProvidersManager.providers).forEach(([serviceName, provider]) => {
    providers.push([provider, { service: servicesManager.services[serviceName] }]);
  });

  const CombinedProviders = ({ children }) => Compose({ components: providers, children });

  customizationService.init(extensionManager);

  const appRoutes = createRoutes({
    modes,
    dataSources,
    extensionManager,
    servicesManager,
    commandsManager,
    hotkeysManager,
    routerBasename,
    showStudyList,
  });

  const authRoutes = oidc ? (
    <OpenIdConnectRoutes
      oidc={oidc}
      routerBasename={routerBasename}
      userAuthenticationService={userAuthenticationService}
    />
  ) : null;

  return (
    <CombinedProviders>
      <AlertConnection />
      {sessionErrorModal}
      {authWarning ? (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#7a2f2f',
            color: '#fff',
            padding: '10px 14px',
            borderRadius: '6px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
            maxWidth: '80vw',
            zIndex: 1100,
          }}
        >
          {authWarning}
        </div>
      ) : null}
      <BrowserRouter basename={routerBasename}>
        {authRoutes}
        {appRoutes}
      </BrowserRouter>
    </CombinedProviders>
  );
}

App.propTypes = {
  config: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      routerBasename: PropTypes.string.isRequired,
      oidc: PropTypes.array,
      whiteLabeling: PropTypes.object,
      extensions: PropTypes.array,
    }),
  ]).isRequired,
  defaultExtensions: PropTypes.array,
  defaultModes: PropTypes.array,
};

App.defaultProps = {
  config: {
    routerBasename: '/',
    showLoadingIndicator: true,
    showStudyList: false,
    oidc: [],
    extensions: [],
  },
  defaultExtensions: [],
  defaultModes: [],
};

export default App;
export { commandsManager, extensionManager, servicesManager };
